// Main Search Service that combines Firestore + Google CSE + AI Category Detection
import { googleSearchService, SearchResult, SearchResponse } from './googleSearchService'
import { aiCategoryService, CategorySuggestion } from './aiCategoryService'
import { db } from '../firebase'
import { collection, getDocs, query, where, orderBy, limit, startAfter, addDoc, Timestamp, deleteDoc, updateDoc, doc, arrayUnion } from 'firebase/firestore'

export interface EnhancedSearchResult extends SearchResult {
  category?: string
  categoryConfidence?: number
  categoryReasoning?: string
  isProcessed?: boolean
  source?: 'firestore' | 'google' | 'hybrid'
  firestoreId?: string
  isActive?: boolean
  sellerId?: string
  createdAt?: any
  updatedAt?: any
}

export interface SearchOptions {
  start?: number
  num?: number
  lr?: string
  hl?: string
  gl?: string
  safe?: string
  enableAICategorization?: boolean
  searchSource?: 'firestore' | 'google' | 'hybrid'
  category?: string
  minPrice?: number
  maxPrice?: number
  brand?: string
}

export interface SearchStats {
  totalResults: number
  processedResults: number
  aiCategorizations: number
  cacheHits: number
  searchTime: number
}

class SearchService {
  private searchStats: SearchStats = {
    totalResults: 0,
    processedResults: 0,
    aiCategorizations: 0,
    cacheHits: 0,
    searchTime: 0
  }

  async search(query: string, options: SearchOptions = {}): Promise<{
    results: EnhancedSearchResult[]
    stats: SearchStats
    hasMore: boolean
  }> {
    const startTime = Date.now()
    
    try {
      console.log('🔍 Starting search:', { query, options })
      
      const searchSource = options.searchSource || 'hybrid'
      let enhancedResults: EnhancedSearchResult[] = []
      let hasMore = false
      let totalResults = 0

      // Skip sample data loading to avoid CORS issues

      // البحث في Firestore أولاً
      if (searchSource === 'firestore' || searchSource === 'hybrid') {
        try {
          const firestoreResults = await this.searchFirestore(query, options)
          if (firestoreResults.length > 0) {
            enhancedResults = [...enhancedResults, ...firestoreResults]
            totalResults += firestoreResults.length
            console.log('📊 Firestore results:', firestoreResults.length)
          }
        } catch (error) {
          console.warn('⚠️ Firestore search failed:', error)
        }
      }

      // إذا لم توجد نتائج كافية، استخدم Google CSE
      if (searchSource === 'google' || searchSource === 'hybrid') {
        // في وضع hybrid، استخدم Google CSE إذا Firestore فارغ أو أعطى نتائج قليلة
        if (searchSource === 'google' || enhancedResults.length < 5) {
          try {
            console.log('🌐 Searching Google CSE for:', query)
            const googleResponse = await googleSearchService.search(query, {
              start: options.start || 1,
              num: options.num || 10,
              lr: options.lr || 'lang_ar',
              hl: options.hl || 'ar',
              gl: options.gl || 'eg',
              safe: options.safe || 'off'
            })
            
            if (googleResponse.items && googleResponse.items.length > 0) {
              const googleResults = await this.processSearchResults(
                googleResponse.items,
                options.enableAICategorization !== false,
                'google'
              )
              enhancedResults = [...enhancedResults, ...googleResults]
              totalResults += parseInt(googleResponse.searchInformation?.totalResults) || 0
              hasMore = !!(googleResponse.queries?.nextPage && googleResponse.queries.nextPage.length > 0)
              console.log('🌐 Google CSE results:', googleResults.length)
            } else {
              console.log('⚠️ No Google CSE results found')
            }
          } catch (error) {
            console.error('❌ Google CSE search failed:', error)
            console.log('⚠️ Continuing without Google CSE results')
          }
        }
      }

      // If still no results, show empty state
      if (enhancedResults.length === 0) {
        console.log('❌ No real results found for query:', query)
        console.log('📊 Search sources tried:', {
          firestore: searchSource === 'firestore' || searchSource === 'hybrid',
          google: searchSource === 'google' || searchSource === 'hybrid'
        })
      }

      // Apply filters
      if (options.category) {
        enhancedResults = enhancedResults.filter(r => r.category === options.category)
      }
      if (options.brand) {
        enhancedResults = enhancedResults.filter(r => r.brand === options.brand)
      }
      if (options.minPrice !== undefined) {
        enhancedResults = enhancedResults.filter(r => (r.price || 0) >= options.minPrice!)
      }
      if (options.maxPrice !== undefined) {
        enhancedResults = enhancedResults.filter(r => (r.price || 0) <= options.maxPrice!)
      }

      // Update stats
      this.searchStats = {
        totalResults: totalResults || enhancedResults.length,
        processedResults: enhancedResults.length,
        aiCategorizations: enhancedResults.filter(r => r.isProcessed).length,
        cacheHits: 0,
        searchTime: Date.now() - startTime
      }

      console.log('✅ Search completed:', this.searchStats)

      return {
        results: enhancedResults,
        stats: this.searchStats,
        hasMore
      }
    } catch (error) {
      console.error('❌ Search service error:', error)
      // Return empty results instead of fake results
      return {
        results: [],
        stats: {
          totalResults: 0,
          processedResults: 0,
          aiCategorizations: 0,
          cacheHits: 0,
          searchTime: 0
        },
        hasMore: false
      }
    }
  }

  private async searchFirestore(searchQuery: string, options: SearchOptions): Promise<EnhancedSearchResult[]> {
    try {
      console.log('🔍 Searching Firestore for:', searchQuery)
      
      const productsRef = collection(db, 'products')
      
      // Get all active products first
      const q = query(
        productsRef,
        where('isActive', '==', true),
        limit(100) // Get more products for better search
      )

      const snapshot = await getDocs(q)
      console.log('📊 Total products in Firestore:', snapshot.docs.length)
      
      // Filter results in memory for better search
      const allDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      const queryText = searchQuery.toLowerCase()
      
      const filteredDocs = allDocs.filter(product => {
        const name = ((product as any).name || '').toLowerCase()
        const description = ((product as any).description || '').toLowerCase()
        const category = ((product as any).category || '').toLowerCase()
        const brand = ((product as any).brand || '').toLowerCase()
        
        return name.includes(queryText) ||
               description.includes(queryText) ||
               category.includes(queryText) ||
               brand.includes(queryText)
      })

      console.log('🔍 Filtered products:', filteredDocs.length)

      // Convert to EnhancedSearchResult format
      const firestoreResults: EnhancedSearchResult[] = filteredDocs.slice(0, options.num || 10).map(product => ({
        id: product.id,
        title: (product as any).name || 'منتج بدون اسم',
        link: `#product-${product.id}`,
        snippet: (product as any).description || 'لا يوجد وصف',
        imageUrl: (product as any).imageUrls?.[0] || 'https://via.placeholder.com/300x200?text=No+Image',
        displayLink: 'karsal.app',
        formattedUrl: `karsal.app/product/${product.id}`,
        category: (product as any).category || 'إلكترونيات',
        brand: (product as any).brand || 'غير محدد',
        price: (product as any).price || 0,
        source: 'firestore',
        firestoreId: product.id,
        isActive: (product as any).isActive,
        sellerId: (product as any).sellerId,
        createdAt: (product as any).createdAt,
        updatedAt: (product as any).updatedAt,
        isProcessed: true
      }))

      console.log('📊 Firestore search completed:', firestoreResults.length, 'results')
      return firestoreResults

    } catch (error) {
      console.error('❌ Firestore search error:', error)
      return []
    }
  }

  private async processSearchResults(
    results: SearchResult[], 
    enableAICategorization: boolean,
    source: 'firestore' | 'google' = 'google'
  ): Promise<EnhancedSearchResult[]> {
    const enhancedResults: EnhancedSearchResult[] = []

    for (const result of results) {
      try {
        const enhanced: EnhancedSearchResult = {
          ...result,
          source,
          isProcessed: false
        }

        // Extract brand from title or snippet
        enhanced.brand = this.extractBrand(result.title, result.snippet)

        // Extract price if available
        enhanced.price = this.extractPrice(result.title, result.snippet)

        // AI Category detection if enabled
        if (enableAICategorization) {
          try {
            const categorySuggestion = await aiCategoryService.detectCategory(
              result.title,
              result.snippet
            )
            
            enhanced.category = categorySuggestion.category
            enhanced.categoryConfidence = categorySuggestion.confidence
            enhanced.categoryReasoning = categorySuggestion.reasoning
            enhanced.isProcessed = true
            
            console.log('🤖 AI categorized:', {
              title: result.title,
              category: categorySuggestion.category,
              confidence: categorySuggestion.confidence
            })
          } catch (error) {
            console.warn('⚠️ AI categorization failed for:', result.title, error)
            // Fallback to simple detection
            enhanced.category = this.simpleCategoryDetection(result.title, result.snippet)
          }
        } else {
          // Simple category detection
          enhanced.category = this.simpleCategoryDetection(result.title, result.snippet)
        }

        enhancedResults.push(enhanced)
      } catch (error) {
        console.error('❌ Error processing result:', result.title, error)
        // Add result without processing
        enhancedResults.push({
          ...result,
          category: 'إلكترونيات',
          isProcessed: false
        })
      }
    }

    return enhancedResults
  }

  private extractBrand(title: string, snippet: string): string {
    const text = `${title} ${snippet}`.toLowerCase()
    
    // Common brands in Egyptian market
    const brands = [
      'شاومي', 'سامسونج', 'أيفون', 'هواوي', 'إل جي', 'سوني', 'ديل', 'إتش بي',
      'لينوفو', 'أسوس', 'إيسر', 'مايكروسوفت', 'أمازون', 'جوجل', 'نوكيا',
      'أوبو', 'فيفو', 'ون بلس', 'ريلمي', 'بوكو', 'تكنو', 'إنفينكس'
    ]

    for (const brand of brands) {
      if (text.includes(brand.toLowerCase())) {
        return brand
      }
    }

    return ''
  }

  private extractPrice(title: string, snippet: string): number {
    const text = `${title} ${snippet}`
    
    // Look for price patterns like "5000 جنيه" or "5000 ج.م" or "5000 EGP"
    const priceRegex = /(\d+(?:,\d{3})*(?:\.\d{2})?)\s*(?:جنيه|ج\.م|EGP|ريال|دولار|USD|دولار أمريكي)/gi
    const matches = text.match(priceRegex)
    
    if (matches && matches.length > 0) {
      const priceStr = matches[0].replace(/[^\d,.]/g, '')
      const price = parseFloat(priceStr.replace(/,/g, ''))
      return isNaN(price) ? 0 : price
    }

    return 0
  }

  private simpleCategoryDetection(title: string, snippet: string): string {
    const text = `${title} ${snippet}`.toLowerCase()
    
    // Mobile and tablets
    if (text.includes('موبايل') || text.includes('هاتف') || text.includes('جوال') || 
        text.includes('تابلت') || text.includes('آيباد') || text.includes('سامسونج') ||
        text.includes('شاومي') || text.includes('أيفون') || text.includes('هواوي')) {
      return 'موبايل وتابلت'
    }

    // TVs
    if (text.includes('تلفزيون') || text.includes('تلفاز') || text.includes('شاشة')) {
      return 'تلفزيونات'
    }

    // Kitchen appliances
    if (text.includes('مطبخ') || text.includes('فرن') || text.includes('ثلاجة') ||
        text.includes('غسالة') || text.includes('خلاط') || text.includes('ميكروويف')) {
      return 'أجهزة المطبخ'
    }

    // Computers
    if (text.includes('كمبيوتر') || text.includes('لابتوب') || text.includes('حاسوب') ||
        text.includes('ماك') || text.includes('ديل') || text.includes('إتش بي')) {
      return 'كمبيوتر'
    }

    // Health and beauty
    if (text.includes('صحة') || text.includes('جمال') || text.includes('عناية') ||
        text.includes('كريم') || text.includes('شامبو') || text.includes('عطر')) {
      return 'الصحة والجمال'
    }

    // Clothes
    if (text.includes('ملابس') || text.includes('قميص') || text.includes('بنطلون') ||
        text.includes('فستان') || text.includes('جاكيت') || text.includes('تي شيرت')) {
      return 'ملابس'
    }

    // Large appliances
    if (text.includes('ثلاجة') || text.includes('غسالة') || text.includes('مكيف') ||
        text.includes('سخان') || text.includes('مروحة')) {
      return 'أجهزة كبيرة'
    }

    // Small appliances
    if (text.includes('خلاط') || text.includes('ميكروويف') || text.includes('محمصة') ||
        text.includes('عصارة') || text.includes('مطحنة')) {
      return 'أجهزة صغيرة'
    }

    // Home tools
    if (text.includes('أدوات') || text.includes('مطرقة') || text.includes('مفك') ||
        text.includes('منشار') || text.includes('مثقاب')) {
      return 'ادوات المنزل'
    }

    // Default to electronics
    return 'إلكترونيات'
  }

  // Get search statistics
  getStats(): SearchStats {
    return { ...this.searchStats }
  }

  // Clear all caches
  clearCaches() {
    googleSearchService.clearCache()
    aiCategoryService.clearCache()
    console.log('🗑️ All search caches cleared')
  }

  // Get cache information
  getCacheInfo() {
    return {
      googleSearch: googleSearchService.getCacheSize(),
      aiCategory: aiCategoryService.getCacheSize()
    }
  }

  // Get available categories
  async getAvailableCategories() {
    try {
      // Try to get categories from Firestore first
      const categoriesRef = collection(db, 'categories')
      const snapshot = await getDocs(categoriesRef)
      
      if (snapshot.docs.length > 0) {
        const categories = snapshot.docs.map(doc => doc.data().name).filter(Boolean)
        console.log('📋 Categories loaded from Firestore:', categories.length)
        return categories
      }
    } catch (error) {
      console.warn('⚠️ Could not load categories from Firestore:', error)
    }
    
    // Fallback to standard categories
    return aiCategoryService.getStandardCategories()
  }

  // Add a new category to Firestore
  async addCategory(categoryName: string): Promise<boolean> {
    try {
      const trimmedName = categoryName.trim()
      if (!trimmedName) {
        console.warn('⚠️ Category name is empty')
        return false
      }

      // Check if category already exists
      const categoriesRef = collection(db, 'categories')
      const existingQuery = query(categoriesRef, where('name', '==', trimmedName))
      const existingSnapshot = await getDocs(existingQuery)
      
      if (!existingSnapshot.empty) {
        console.warn('⚠️ Category already exists:', trimmedName)
        return false
      }

      // Add new category to Firestore
      await addDoc(categoriesRef, {
        name: trimmedName,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      })
      
      console.log('✅ Category added to Firestore:', trimmedName)
      return true
    } catch (error) {
      console.error('❌ Error adding category to Firestore:', error)
      throw error
    }
  }

  // Delete a category from Firestore
  async deleteCategory(categoryName: string): Promise<boolean> {
    try {
      const trimmedName = categoryName.trim()
      if (!trimmedName) {
        console.warn('⚠️ Category name is empty')
        return false
      }

      // Find category document
      const categoriesRef = collection(db, 'categories')
      const categoryQuery = query(categoriesRef, where('name', '==', trimmedName))
      const snapshot = await getDocs(categoryQuery)
      
      if (snapshot.empty) {
        console.warn('⚠️ Category not found:', trimmedName)
        return false
      }

      // Delete category
      for (const categoryDoc of snapshot.docs) {
        await deleteDoc(doc(db, 'categories', categoryDoc.id))
      }
      
      console.log('✅ Category deleted from Firestore:', trimmedName)
      return true
    } catch (error) {
      console.error('❌ Error deleting category from Firestore:', error)
      throw error
    }
  }

  // Update a category in Firestore
  async updateCategory(oldName: string, newName: string): Promise<boolean> {
    try {
      const trimmedOldName = oldName.trim()
      const trimmedNewName = newName.trim()
      
      if (!trimmedOldName || !trimmedNewName) {
        console.warn('⚠️ Category names are invalid')
        return false
      }

      if (trimmedOldName === trimmedNewName) {
        console.warn('⚠️ Category names are the same')
        return false
      }

      // Check if new name already exists
      const categoriesRef = collection(db, 'categories')
      const existingQuery = query(categoriesRef, where('name', '==', trimmedNewName))
      const existingSnapshot = await getDocs(existingQuery)
      
      if (!existingSnapshot.empty) {
        console.warn('⚠️ New category name already exists:', trimmedNewName)
        return false
      }

      // Find category document
      const categoryQuery = query(categoriesRef, where('name', '==', trimmedOldName))
      const snapshot = await getDocs(categoryQuery)
      
      if (snapshot.empty) {
        console.warn('⚠️ Category not found:', trimmedOldName)
        return false
      }

      // Update category
      for (const categoryDoc of snapshot.docs) {
        await updateDoc(doc(db, 'categories', categoryDoc.id), {
          name: trimmedNewName,
          previousNames: arrayUnion(trimmedOldName),
          updatedAt: Timestamp.now()
        })
      }
      
      console.log('✅ Category updated in Firestore:', trimmedOldName, '->', trimmedNewName)
      return true
    } catch (error) {
      console.error('❌ Error updating category in Firestore:', error)
      throw error
    }
  }

  // Load sample data from local JSON file (no CORS issues)
  async loadSampleData() {
    try {
      // Use local JSON file instead of external URL to avoid CORS
      const response = await fetch('/xiaomi_note_14.json')
      const data = await response.json()
      
      if (data.items && data.items.length > 0) {
        console.log('📦 Sample data loaded:', data.items.length, 'items')
        return data.items.map((item: any, index: number) => ({
          id: `sample-${index}`,
          title: item.title || '',
          link: item.link || '',
          snippet: item.snippet || '',
          imageUrl: item.pagemap?.cse_image?.[0]?.src || item.pagemap?.cse_thumbnail?.[0]?.src || '',
          displayLink: item.displayLink || '',
          formattedUrl: item.formattedUrl || '',
          category: this.simpleCategoryDetection(item.title || '', item.snippet || ''),
          brand: this.extractBrand(item.title || '', item.snippet || ''),
          price: this.extractPrice(item.title || '', item.snippet || ''),
          source: 'google' as const,
          isProcessed: true
        }))
      }
    } catch (error) {
      console.warn('⚠️ Could not load sample data (CORS issue):', error)
      // Return empty array instead of throwing error
    }
    
    return []
  }

  // Create simple fallback results for testing
  private createSimpleFallbackResults(query: string): EnhancedSearchResult[] {
    const fallbackResults: EnhancedSearchResult[] = [
      {
        id: 'fallback-1',
        title: `منتج ${query} - نتيجة تجريبية`,
        link: 'https://www.amazon.eg',
        snippet: `هذا منتج تجريبي للاختبار - ${query}. جرب البحث في Google أو Amazon للحصول على نتائج حقيقية.`,
        imageUrl: 'https://via.placeholder.com/300x200?text=Test+Product',
        displayLink: 'amazon.eg',
        formattedUrl: 'amazon.eg/search',
        category: 'إلكترونيات',
        brand: 'تجريبي',
        price: 0,
        source: 'google',
        isProcessed: true,
        categoryConfidence: 0.5,
        categoryReasoning: 'نتيجة تجريبية للاختبار'
      },
      {
        id: 'fallback-2',
        title: `منتج ${query} - جرب البحث في Google`,
        link: 'https://www.google.com/search?q=' + encodeURIComponent(query),
        snippet: `للحصول على نتائج حقيقية، جرب البحث في Google عن: ${query}`,
        imageUrl: 'https://via.placeholder.com/300x200?text=Google+Search',
        displayLink: 'google.com',
        formattedUrl: 'google.com/search',
        category: 'بحث',
        brand: 'Google',
        price: 0,
        source: 'google',
        isProcessed: true,
        categoryConfidence: 0.5,
        categoryReasoning: 'رابط بحث في Google'
      }
    ]

    return fallbackResults
  }

  // Create mock results for testing when no real data is available
  private createMockResults(query: string): EnhancedSearchResult[] {
    const mockResults: EnhancedSearchResult[] = [
      {
        id: 'mock-1',
        title: `منتج ${query} تجريبي 1`,
        link: '#mock-1',
        snippet: `هذا منتج تجريبي للاختبار - ${query}`,
        imageUrl: 'https://via.placeholder.com/300x200?text=Mock+Product+1',
        displayLink: 'karsal.app',
        formattedUrl: 'karsal.app/mock-1',
        category: 'موبايل وتابلت',
        brand: 'شاومي',
        price: 5000,
        source: 'firestore',
        isProcessed: true,
        categoryConfidence: 0.9,
        categoryReasoning: 'تم تحديد الفئة بناءً على الكلمات المفتاحية'
      },
      {
        id: 'mock-2',
        title: `منتج ${query} تجريبي 2`,
        link: '#mock-2',
        snippet: `منتج تجريبي آخر للاختبار - ${query}`,
        imageUrl: 'https://via.placeholder.com/300x200?text=Mock+Product+2',
        displayLink: 'karsal.app',
        formattedUrl: 'karsal.app/mock-2',
        category: 'إلكترونيات',
        brand: 'سامسونج',
        price: 8000,
        source: 'google',
        isProcessed: true,
        categoryConfidence: 0.8,
        categoryReasoning: 'تم تحديد الفئة بناءً على اسم المنتج'
      },
      {
        id: 'mock-3',
        title: `منتج ${query} تجريبي 3`,
        link: '#mock-3',
        snippet: `منتج تجريبي ثالث للاختبار - ${query}`,
        imageUrl: 'https://via.placeholder.com/300x200?text=Mock+Product+3',
        displayLink: 'karsal.app',
        formattedUrl: 'karsal.app/mock-3',
        category: 'أجهزة كبيرة',
        brand: 'إل جي',
        price: 12000,
        source: 'hybrid',
        isProcessed: true,
        categoryConfidence: 0.7,
        categoryReasoning: 'تم تحديد الفئة بناءً على الوصف'
      }
    ]

    // Filter mock results based on query
    return mockResults.filter(result => 
      result.title.toLowerCase().includes(query.toLowerCase()) ||
      result.snippet.toLowerCase().includes(query.toLowerCase()) ||
      (result.category || '').toLowerCase().includes(query.toLowerCase()) ||
      (result.brand || '').toLowerCase().includes(query.toLowerCase())
    )
  }
}

export const searchService = new SearchService()
